<template>
    <div style="min-height: fit-content;">
        <GridLayout
            v-if="layoutData"
            id="grid"
            v-model:layout="layoutData"
            :colNum="3"
            :rowHeight="windowHeight * 0.44"
            :isResizable="false"
        >
            <GridItem
                v-for="item in layoutData"
                :key="item.i"
                :x="item.x"
                :y="item.y"
                :w="item.w"
                :h="item.h"
                :i="item.i"
                dragAllowFrom=".vue-draggable-handle"
                dragIgnoreFrom=".no-drag"
                class="rounded-borders flex column"
                :class="{
                    'bg-grey-4': editMode && !item.name,
                    'grid-item-blue': editMode && graphObj === item.i,
                    'grid-item': item.name,
                    'grid-item-transparent': editMode && !item.name,
                }"
            >
                <div
                    class="filter-indicator flex TitleContainer q-ma-xs row gap"
                    @mouseenter="showTabs = true"
                    @mouseleave="showTabs = false"
                    @focus="showTabs = true"
                    @blur="showTabs = false"
                >
                    <span
                        v-if="item.name"
                        class="text-bold Title"
                        :class="{ Cursor: editMode }"
                        @click="handleGraphClick(item.i)"
                        @keydown.enter="handleGraphClick(item.i)"
                    >
                        {{ item.name }}
                    </span>
                    <span
                        v-else
                        class="text-bold Title"
                    />
                    <FilterTabs
                        v-if="item.name"
                        :visible="showTabs"
                        :generalFilters="generalFilters"
                        :graphFilters="layoutData.find(values => values.i === item.i)?.ownFilters"
                        :dashboardFilters="dashboardFilters"
                        :allFilters="allFilters"
                    />
                    <div class="flex row Icons">
                        <div
                            v-if="!editMode && item.name"
                            class="q-mr-xs"
                        >
                            <QBtn
                                dense
                                size="12px"
                                icon="zoom_in"
                                color="primary"
                                class="Button"
                                @click="openModal(item)"
                            >
                                <QTooltip>
                                    {{ $t('dashboards.dashboardButtons.zoom') }}
                                </QTooltip>
                            </QBtn>
                        </div>
                        <div
                            v-if="item.name && editMode"
                            class="q-mr-xs"
                        >
                            <QBtn
                                dense
                                size="12px"
                                icon="delete"
                                color="primary"
                                class="Button"
                                @click="deleteGraph(item.i)"
                            >
                                <QTooltip>
                                    {{ $t('dashboards.dashboardButtons.remove') }}
                                </QTooltip>
                            </QBtn>
                        </div>
                        <div
                            v-if="editMode"
                            class="q-mr-xs"
                        >
                            <QBtn
                                dense
                                :flat="!item.name ? true : false"
                                :textColor="!item.name ? 'black' : 'white'"
                                class="vue-draggable-handle Button"
                                size="12px"
                                icon="open_with"
                                color="primary"
                            >
                                <QTooltip>
                                    {{ $t('dashboards.dashboardButtons.move') }}
                                </QTooltip>
                            </QBtn>
                        </div>
                    </div>
                </div>
                <div
                    class="no-drag full-height full-width"
                >
                    <div
                        v-if="!item.name && editMode"
                        class="full-height full-width q-pa-sm rounded-borders flex items-center justify-center"
                    >
                        <div
                            class="q-pa-xl text-white text-h2 text-bold text-center"
                        >
                            +
                        </div>
                    </div>
                    <GraphComponent
                        v-if="item.name"
                        :generalFilters="generalFilters"
                        :generalFilterSets="generalFilterSets"
                        :generalOperator="generalOp"
                        :graphValues="item.graphValues"
                        :graphFilters="layoutData.find(values => values.i === item.i)?.ownFilters"
                        :dashboardFilters="dashboardFilters"
                        :graphObj="graphObj"
                        :chartCreationProps="item"
                        @mouseenter="showTabs = true"
                        @focus="showTabs = true"
                        @mouseleave="showTabs = false"
                        @blur="showTabs = false"
                    />
                </div>
            </GridItem>
        </GridLayout>
        <QDialog v-model="modalMode">
            <QCard style="width: 60vw; max-width: 60vw;">
                <QBar class="bg-primary text-white">
                    <div class="text-subtitle1 text-weight-medium">
                        {{ graphModal.name }}
                    </div>
                    <QSpace />
                    <QBtn
                        v-close-popup
                        dense
                        flat
                        color="white"
                        icon="close"
                    />
                </QBar>
                <QCardSection>
                    <GraphComponent
                        :generalFilters="generalFilters"
                        :generalFilterSets="generalFilterSets"
                        :generalOperator="generalOp"
                        :graphFilters="graphFilters"
                        :dashboardFilters="dashboardFilters"
                        :chartCreationProps="graphModal"
                    />
                </QCardSection>
            </QCard>
        </QDialog>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { GridLayout, GridItem } from 'grid-layout-plus'
import {
    Api,
    apiRequest,
    notifyError,
    notifySuccess,
} from '@/api'
import { filtersStorageMixin } from '@/mixins'
import GraphComponent from './GraphComponent.vue'
import FilterTabs from './FilterTabs.vue'
import {
    ChartTypes,
    GRAFICAS,
    ChartGroups,
} from '../utils'

export default {
    name: 'GridGraphs',
    components: {
        GridItem,
        GridLayout,
        GraphComponent,
        FilterTabs,
    },
    mixins: [filtersStorageMixin],
    props: {
        processId: VueTypes.string,
        dashboard: VueTypes.object,
    },
    emits: ['updateDashboardContent', 'drawer-state-changed'],
    data() {
        return {
            DEFAULT_VALUES: {
                freqDuration: {
                    duration: {
                        key: "MONTH",
                        label: this.$t("visualization.customPlotPanels.attributesInfo.month"),
                    },
                    rangeSel: {
                        key: "START",
                        label: this.$t("visualization.customPlotPanels.attributesInfo.start"),
                    },
                },
                sortParams: {
                    sortDirection: { key: "ASCENDING", label: "Ascending" },
                },
            },
            windowHeight: window.innerHeight - 100,
            layoutData: [],
            graphObj: null,
            editMode: false,
            sideTab: "graphsList",
            dashboardFilters: [],
            graphFilters: [],
            graphPositions: [],
            chartTypes: ChartTypes,
            chartGroups: ChartGroups,
            graphModal: false,
            modalMode: false,
            allFilters: [],
            attribute: null,
            attributes: [],
            categoricalAttributes: [],
            numericAttributes: [],
            optionAttributes: [],
            numericAttribute: false,
            numericAttrMin: null,
            numericAttrMax: null,
            numericRange: { min: 0, max: 0 },
            graficas: GRAFICAS,
            backupData: {},
            showTabs: false,
        }
    },
    computed: {
        generalFilters() {
            const { filters } = this.getActiveFiltersApiParts()
            return filters || []
        },
        generalFilterSets() {
            const { filterSets } = this.getActiveFiltersApiParts()
            return filterSets || []
        },
        generalOp() {
            const { operator } = this.getActiveFiltersApiParts()
            return operator || 'AND'
        },
        updatedGraficas() {
            return this.layoutData.map((grafica) => ({
                ...grafica,
                label: grafica.label,
            }))
        },
    },
    mounted() {
        this.initializeLayout()
        this.getAllFilters()
        this.getAttributes()
        this.emitDrawerState()
    },
    methods: {
        emitDrawerState() {
            this.$emit('drawer-state-changed', {
                editMode: this.editMode,
                graphObj: this.graphObj,
                sideTab: this.sideTab,
                layoutData: this.layoutData,
                dashboardFilters: this.dashboardFilters,
                allFilters: this.allFilters,
                attributes: this.attributes,
                categoricalAttributes: this.categoricalAttributes,
                numericAttributes: this.numericAttributes,
            })
        },

        enterEditMode() {
            if (!this.editMode) this.handleEditMode()
        },
        saveEditMode() {
            if (this.editMode) this.handleEditMode()
        },
        cancelEditMode() {
            if (this.editMode) this.handleCloseSidebar()
        },
        setSideTab(tab) {
            if (!tab) return
            if (tab === this.sideTab) return
            this.sideTab = tab
            this.emitDrawerState()
        },
        addGraphFromPanel({ type, group } = {}) {
            if (!type || !group) return
            this.addGraph(type, group)
            this.emitDrawerState()
        },
        renameCurrentGraph({ name } = {}) {
            if (!name) return
            this.editName({ name })
            this.emitDrawerState()
        },
        async getAttributes() {
            this.attributesLoading = true
            const { processId } = this
            apiRequest(Api().datasets.getConfigurations({ datasetId: processId }))
                .then(({ data }) => {
                    const [datasetConfig] = data
                    this.attributes = datasetConfig.columns
                })
                .catch(notifyError)
                .finally(() => {
                    this.getCategoricalAttributes()
                })
        },
        async getCategoricalAttributes() {
            const { processId } = this
            const { filters, filterSets } = this.getActiveFiltersApiParts()
            const params = { filters, filterSets }
            apiRequest(Api().visualizations.attributes({ processId, params }))
                .then((res) => {
                    this.categoricalAttributes = res
                })
                .catch(notifyError)
                .finally(() => {
                    this.getNumericAttributes()
                })
        },
        async getNumericAttributes() {
            const { processId } = this
            const { filters, filterSets, operator } = this.getActiveFiltersApiParts()
            const params = { filters, filterSets, operator }
            return apiRequest(Api().visualizations.numericalAttributes({ processId, params }))
                .then((res) => {
                    this.numericAttributes = res
                })
                .catch(notifyError)
                .finally(() => {
                    if (this.categoricalAttributes.length !== 0) {
                        this.attrSel()
                    }
                    this.getActivitiesOptions()
                    this.emitDrawerState()
                })
        },
        initializeLayout() {
            if (this.dashboard.size) {
                for (let i = 0; i < this.dashboard.size[0]; i += 1) {
                    for (let j = 0; j < this.dashboard.size[1]; j += 1) {
                        this.graphPositions.push([i, j])
                        this.layoutData.push({
                            x: i,
                            y: j,
                            w: 1,
                            h: 1,
                            i: Math.random() * (9999 - 1) + 1,
                            empty: true,
                        })
                    }
                }
                this.graphPositions = this.graphPositions.reverse()
            }
            if (this.dashboard.content.length > 0) {
                for (let i = 0; i < this.dashboard.content.length; i += 1) {
                    const {
                        position, size, identifier, ownFilters, ...rest
                    } = this.dashboard.content[i]
                    const obj = {
                        x: position[0],
                        y: position[1],
                        w: size[0],
                        h: size[1],
                        i: identifier,
                        ownFilters: ownFilters !== "" ? JSON.parse(ownFilters) : [],
                        empty: false,
                        ...rest,
                    }
                    if (this.dashboard.dashboardFilters !== "") {
                        obj.dashboardFilters = JSON.parse(this.dashboard.dashboardFilters)
                        this.dashboardFilters = JSON.parse(this.dashboard.dashboardFilters)
                    }
                    const graphValuesStr = this.dashboard.content[i].graphValues
                    if ([ChartTypes.BAR, ChartTypes.HEATMAP].includes(rest.graphType)) {
                        const graphValues = JSON.parse(graphValuesStr)
                        obj.sortBy = graphValues.sortBy
                        obj.sortDirection = graphValues.sortDirection
                    }
                    const index = this.layoutData.findIndex((item) => item.x === position[0] && item.y === position[1])
                    this.layoutData.splice(index, 1, obj)
                    this.graphPositions.splice(this.graphPositions.indexOf([position[0], position[1]]))
                }
            }

            this.emitDrawerState()
        },
        attrSel() {
            const numericTypes = ['BOOLEAN', 'SHORT', 'INTEGER', 'LONG', 'DECIMAL']
            const isNumericType = (item) => numericTypes.includes(item.type)
            const optionAttr = this.attributes.filter((item) => {
                const isNumericTypeConts = isNumericType(item)
                const isStringAndInCategorical = item.type === 'STRING'
                    && this.categoricalAttributes.some((cat) => cat.name === item.name)
                return isNumericTypeConts || isStringAndInCategorical
            })
            this.optionAttributes = optionAttr
            const attr = this.optionAttributes[0].name
            this.attribute = attr

            const typeAttr = this.optionAttributes.find((obj) => obj.name === this.attribute)
            if (typeAttr.type === 'STRING' || typeAttr.type === 'BOOLEAN') {
                this.numericAttribute = false
            } else {
                this.numericAttribute = true
            }

            if (isNumericType(this.optionAttributes[0]) && this.numericAttributes.length > 0) {
                const numAttr = this.numericAttributes.find((a) => a.name === this.optionAttributes[0].name)
                this.numericAttrMin = Number(numAttr.values.at(0))
                this.numericAttrMax = Number(numAttr.values.at(1))
                this.numericRange = { min: this.numericAttrMin, max: this.numericAttrMax }
            }
        },
        getActivitiesOptions() {
            const { processId } = this.$route.params
            const { filters, filterSets, operator } = this.getActiveFiltersApiParts()
            const params = {
                filters,
                filterSetsUUIDs: filterSets,
                generalOperator: operator,
                arcs: true,
            }
            apiRequest(Api().visualizations.splitFilteredActivities({ processId, params }))
                .then((data) => {
                    this.optionActivities = (data.allActivities).filter((item) => !['START', 'END'].includes(item)).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    const firstActivity = this.optionActivities[0]
                    this.activities = [firstActivity]
                })
                .catch(notifyError)
        },
        handleEditMode() {
            if (this.editMode === false) {
                this.editMode = true
                this.backupData = {
                    layout: [...this.layoutData],
                    positions: [...this.graphPositions],
                }
                this.emitDrawerState()
            } else {
                this.saveDashboard()
                this.editMode = false
                this.graphObj = ""
                this.sideTab = 'graphsList'
                this.backupData = {}
                this.emitDrawerState()
            }
        },
        handleGraphClick(obj) {
            if (this.editMode) {
                if (this.graphObj === obj) {
                    this.graphObj = ""
                    this.sideTab = 'graphsList'
                } else {
                    this.graphObj = obj
                    this.sideTab = 'graph'
                }
                this.emitDrawerState()
            }
        },
        editName(newName) {
            if (newName.name) {
                const elem = this.layoutData.find((el) => el.i === this.graphObj)
                elem.name = newName.name
                this.emitDrawerState()
            }
        },
        async saveDashboard() {
            const layout = this.layoutData.filter((item) => item.name)
            const params = {
                ...this.dashboard,
                dashboardFilters: JSON.stringify(this.dashboardFilters),
                content: layout.length > 0
                    ? layout.map(({
                        x, y, w, h, i, ownFilters, dashboardFilters, ...rest
                    }) => {
                        const item = {
                            position: [x, y],
                            size: [w, h],
                            identifier: i,
                            ...rest,
                        }
                        if (ownFilters && Object.keys(ownFilters).length > 0) {
                            item.ownFilters = JSON.stringify(ownFilters)
                        } else {
                            item.ownFilters = ""
                        }

                        return item
                    })
                    : [],
                size: [3, 2],
            }
            apiRequest(Api().datasets.editDashboard({ datasetId: this.processId, dashboardId: this.dashboard.identifier, params }))
                .then(() => {
                    this.$emit('updateDashboardContent')
                    notifySuccess(this.$t('dashboards.saveDashboard'))
                })
                .catch(notifyError)
        },
        getAllFilters() {
            apiRequest(Api().visualizations.filters())
                .then((res) => {
                    this.allFilters = res
                })
        },
        addGraph(type, group) {
            if (this.layoutData.filter((el) => el.empty === true).length > 0) {
                const rID = Math.floor(Math.random() * (9999 - 1) + 1)
                const pos = this.layoutData.filter((el) => el.empty === true)[0]
                const typeGraph = this.getChartType(type, group)
                const hasSortBy = [ChartTypes.BAR, ChartTypes.HEATMAP].includes(typeGraph)
                const params = {
                    name: this.getLabel(group, type),
                    x: pos.x,
                    y: pos.y,
                    w: 1,
                    h: 1,
                    i: rID,
                    empty: false,
                    graphType: typeGraph,
                    affectedByGlobalFilters: true,
                    affectedByDashboardFilters: true,
                    ownFilters: "",
                    graphValues: "",
                    graphGroup: group,
                }
                if (hasSortBy) {
                    params.sortBy = type
                }
                params.graphValues = JSON.stringify(this.handleGraphValues(params))
                const index = this.layoutData.findIndex((item) => item.x === pos.x && item.y === pos.y)
                this.layoutData.splice(index, 1, params)
            }

            this.emitDrawerState()
        },
        deleteGraph(id) {
            const index = this.layoutData.findIndex((item) => item.i === id)

            const el = this.layoutData.find((item) => item.i === id)

            const obj = {
                x: el.x,
                y: el.y,
                w: 1,
                h: 1,
                i: Math.random() * (9999 - 1) + 1,
                empty: true,
            }

            if (index > -1) this.layoutData.splice(index, 1, obj)
            this.graphObj = ""
            this.sideTab = 'graphsList'

            this.emitDrawerState()
        },
        getLabel(group, type) {
            return `${this.$t(`dashboards.graphTitles.${group}`)}: ${type ? this.$t(`dashboards.graphSubtitles.${type}`) : ''}`
        },
        openModal(item) {
            this.graphModal = item
            this.modalMode = true
        },
        getChartType(contentKey, key) {
            switch (key) {
                case "TRANSITIONS":
                    return ChartTypes.HEATMAP
                case "ACTIVITIES":
                    return ChartTypes.BAR
                case "FREQUENCY_AND_DURATION":
                    return ChartTypes.FREQUENCY_AND_DURATION
                case "ATTRIBUTE":
                    return ChartTypes.ATTRIBUTE
                case "VARIANTS":
                    return contentKey === "frequencyvsaverage" ? ChartTypes.SCATTER : ChartTypes.BAR
                case "OTHERS":
                    return ChartTypes.FREQUENCY_AND_DURATION
                default:
                    return null
            }
        },
        updateGraph(params) {
            const { index, name, ...rest } = params
            const position = this.layoutData.findIndex((obj) => obj.i === index)
            this.layoutData[position] = {
                ...this.layoutData[position],
                graphValues: JSON.stringify(rest),
                name,
                ...rest,
            }

            this.emitDrawerState()
        },
        updateTriggers(triggers) {
            if (this.graphObj) {
                this.graphFilters = triggers
                const position = this.layoutData.findIndex((obj) => obj.i === this.graphObj)
                this.layoutData[position].ownFilters = this.graphFilters.length > 0
                    ? [...this.graphFilters]
                    : []
            } else {
                this.dashboardFilters = triggers
                this.layoutData = this.layoutData.map((item) => ({
                    ...item,
                    dashboardFilters: {
                        ...item.dashboardFilters,
                        ...this.dashboardFilters,
                    },
                }))
            }

            this.emitDrawerState()
        },
        handleGraphValues(params) {
            const hasSortBy = [ChartTypes.BAR, ChartTypes.HEATMAP].includes(params.graphType)
            let sortByOption = null
            if (hasSortBy) {
                sortByOption = GRAFICAS[0].content.find((item) => item.key === params.sortBy) ?? GRAFICAS[0].content[0]
            }
            switch (params.graphType) {
                case ChartTypes.BAR:
                    return {
                        sortBy: sortByOption.key,
                        sortDirection: this.DEFAULT_VALUES.sortParams.sortDirection.key,
                    }
                case ChartTypes.FREQUENCY_AND_DURATION:
                    return {
                        duration: this.DEFAULT_VALUES.freqDuration.duration.key,
                        caseType: this.DEFAULT_VALUES.freqDuration.rangeSel.key,
                    }
                case ChartTypes.HEATMAP:
                    return {
                        sortBy: sortByOption.key,
                        sortDirection: this.DEFAULT_VALUES.sortParams.sortDirection.key,
                    }
                case ChartTypes.ATTRIBUTE:
                    return {
                        attribute: this.attribute,
                        numericAttribute: this.numericAttribute,
                        optionAttributes: this.optionAttributes,
                        optionActivities: this.optionActivities,
                        numericRange: this.numericRange,
                        numericAttrMin: this.numericRange.min,
                        numericAttrMax: this.numericRange.max,
                    }
                default:
                    return {
                    }
            }
        },
        getGraphName(id) {
            const elem = this.layoutData.find((el) => el.i === id)
            return elem.name
        },
        handleCloseSidebar() {
            this.editMode = false
            this.graphObj = ""
            this.sideTab = 'graphsList'
            this.graphPositions = this.backupData.positions
            this.layoutData = this.backupData.layout
            this.backupData = {}

            this.emitDrawerState()
        },
    },
}
</script>

<style lang="scss">

.primary {
    color: var(--q-primary) !important;
}
.grid-item {
    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.3) 0px 1px 3px 1px;
    flex-wrap:nowrap !important;
}
.grid-item-transparent {
    box-shadow: rgba(60, 64, 67, 0) 0px 1px 2px 0px, rgba(60, 64, 67, 0) 0px 1px 3px 1px;
    flex-wrap:nowrap !important;
}
.grid-item-blue {
    box-shadow: rgba(42, 143, 229) 0px 0px 10px 4px;
    flex-wrap:nowrap !important;
}
.graph-icons-box {
    color: black;
    border-radius: 20px;
    cursor: pointer;
}
.border-blue {
    border: 2px solid var(--q-primary);
}
.border-transparent {
    border: 2px solid transparent;
}
.TitleContainer {
  flex-wrap:nowrap !important;
  white-space: nowrap;

  .Title {
    flex-grow: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .Cursor {
    cursor: pointer;
  }
  .Icons {
    flex-wrap: nowrap;
  }
}
.Button {
    aspect-ratio: 1/1;

    .q-icon {
        font-size: small !important;
    }
}
.Card .Body {
    min-width: 500px
}
</style>
